import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActiveDescendantKeyManager, FocusKeyManager, ListKeyManager } from '@angular/cdk/a11y';
import { ENTER } from '@angular/cdk/keycodes';
import { FormControl } from '@angular/forms';
import { distinctUntilChanged } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, concat, of } from 'rxjs';
import { isEqual, orderBy } from 'lodash-es';
import { FilterSelectOption } from '@nimbus-library/features/shared/components/nimbus-grid-filter/domain/filter-select.model';
import { NimbusFilterSelectListItemComponent } from '@nimbus-library/features/shared/components/nimbus-grid-filter/nimbus-filter-select-list-item/nimbus-filter-select-list-item.component';
import { WindowEventsService } from '@nimbus-library/features/shared/services/window-events.service';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'nimbus-nimbus-filter-select-list',
  templateUrl: './nimbus-filter-select-list.component.html',
  styleUrls: ['./nimbus-filter-select-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NimbusFilterSelectListComponent implements OnChanges, AfterViewInit, OnDestroy {
  @ViewChildren(NimbusFilterSelectListItemComponent) public items: QueryList<NimbusFilterSelectListItemComponent>;
  @ViewChild('searchInput') public searchInput: ElementRef<HTMLInputElement>;

  @Input() public focusable: boolean = true;
  @Input() public searchable: boolean = true;
  @Input() public typeAheadEnabled: boolean = true;
  @Input() public options: FilterSelectOption[];

  @Output() public selectionChanged: EventEmitter<FilterSelectOption[]> = new EventEmitter();
  @Output() public searchControlValueChanged: EventEmitter<string> = new EventEmitter();

  @HostBinding('class.no-search') public noSearch: boolean = false;

  private keyManager: ListKeyManager<NimbusFilterSelectListItemComponent>;
  private optionsChangedTrigger$: BehaviorSubject<void> = new BehaviorSubject(null);

  private get optionsInternal(): FilterSelectOption[] {
    return (this.options ?? []).filter((x) => !!x?.id);
  }

  public get optionsInternalLength(): number {
    return this.optionsInternal?.length ?? 0;
  }

  public selectedOptionsLookup: Set<string | null> = new Set<string | null>();
  public hasSelection: boolean = false;
  public selection: FilterSelectOption[];
  public searchControl: FormControl<string> = new FormControl<string>('');
  public options$: BehaviorSubject<FilterSelectOption[]> = new BehaviorSubject([]);
  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;

  @Input() public set selectedOptions(val: string[]) {
    this.selectedOptionsLookup = val.reduce((acc, curr) => {
      acc.add(curr);
      return acc;
    }, new Set<string | null>());
    this.hasSelection = val.length > 0;
  }

  public constructor(private readonly windowEventsService: WindowEventsService) {}

  public get nullOption(): FilterSelectOption {
    return this.options?.find((o) => o.id == null);
  }

  public get hasNullSelection(): boolean {
    return this.selectedOptionsLookup.has(this.nullOption?.id);
  }

  @HostListener('keydown', ['$event'])
  public manage(event: KeyboardEvent): void {
    this.keyManager.onKeydown(event);
  }

  public trackByFn = (_index: number, item: FilterSelectOption): string => item.id;

  public ngOnChanges(changes: SimpleChanges): void {
    for (const key of this.selectedOptionsLookup.keys()) {
      if (!this.options.find((item) => item.id === key)) {
        this.options.push({
          id: key,
          name: key,
        });
      }
    }

    if (changes.options?.previousValue && !isEqual(changes.options.previousValue, changes.options.currentValue)) {
      this.optionsChangedTrigger$.next();
    }
  }

  public ngAfterViewInit(): void {
    this.noSearch = !this.searchable && this.optionsInternal.length <= 2;
    const keyManagerBase = (this.focusable ? new FocusKeyManager(this.items) : new ActiveDescendantKeyManager(this.items)).withWrap();
    this.keyManager = this.typeAheadEnabled ? keyManagerBase.withTypeAhead() : keyManagerBase;

    this.windowEventsService
      .getKeyDown$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        this.keyManager.onKeydown(event);
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.setClickStyle();
        }
      });

    this.windowEventsService
      .getKeyUp$()
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        if (event.keyCode === ENTER && this.keyManager.activeItem) {
          this.keyManager.activeItem.onClick();
        }
      });

    this.searchControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => this.searchControlValueChanged.emit(value));

    combineLatest([this.optionsChangedTrigger$, concat(of(''), this.searchControl.valueChanges)])
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe(([_, value]) => {
        const available = this.optionsInternal.filter((option) => option.id != null);
        this.options$.next(value ? available.filter((option) => option.name.toLowerCase().includes(value.toLowerCase())) : available);
      });

    this.selection = orderBy(
      this.optionsInternal.filter((option) => this.selectedOptionsLookup.has(option.id)),
      (c) => c.name.toLowerCase(),
      'asc',
    );
    this.searchInput?.nativeElement?.focus();
  }

  public ngOnDestroy(): void {
    this.searchControlValueChanged.emit(null);
  }

  public toggleSelection(): void {
    if (this.hasSelection) {
      this.clearAll();
    } else {
      this.selectAll(this.options);
    }
  }

  public toggleNullSelection(): void {
    if (!this.nullOption) {
      return;
    }
    this.onFilterSelect({ selected: !this.hasNullSelection, option: this.nullOption });
  }

  public onFilterSelect({ selected, option }: { selected: boolean; option: FilterSelectOption }): void {
    const selection = orderBy(
      this.options.filter((o) => this.selectedOptionsLookup.has(o.id)),
      (c) => c.name,
      'asc',
    );
    if (selected) {
      this.selectionChanged.emit([...selection, option]);
    } else {
      this.selectionChanged.emit(selection.filter((selectedOption) => selectedOption.id !== option.id));
    }
  }

  public onRemoveSearch(): void {
    this.searchControl.setValue(null);
  }

  private selectAll(selection: FilterSelectOption[]): void {
    this.selectionChanged.emit(selection);
  }

  private clearAll(): void {
    this.selectionChanged.emit([]);
  }
}
